/**
 * Hook for real-time chat messages and sending.
 * Subscribes to the chatMessages collection filtered by chat_id, ordered by timestamp.
 */

import chatService from "@features/messaging/services/chatService";
import type { Message } from "@features/messaging/stores/messagingStore";
import { toMessage } from "@features/messaging/utils/chatMessageUtils";
import logger from "@logger";
import { useCallback, useEffect, useRef, useState } from "react";

const SUBSCRIPTION_ERROR_MESSAGE = "Could not load messages.";

export function useChatMessages(chatId: string, currentUserId: string) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [error, setError] = useState<string | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  const subscribe = useCallback(() => {
    unsubscribeRef.current?.();

    if (!chatId || !currentUserId) {
      unsubscribeRef.current = null;
      return;
    }

    setError(null);

    unsubscribeRef.current = chatService.subscribeToMessages(
      chatId,
      (data) => {
        logger.debug("Received messages", { chatId, count: data.length });
        setMessages(data.map(toMessage));
        setError(null);
      },
      (subscriptionError) => {
        logger.error("Chat messages subscription failed", {
          chatId,
          code: "code" in subscriptionError ? subscriptionError.code : undefined,
          message: subscriptionError.message,
        });
        setError(SUBSCRIPTION_ERROR_MESSAGE);
      }
    );
  }, [chatId, currentUserId]);

  useEffect(() => {
    subscribe();
    return () => {
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
    };
  }, [subscribe]);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) {
        return;
      }
      await chatService.sendMessage(chatId, currentUserId, trimmed);
    },
    [chatId, currentUserId]
  );

  return { messages, sendMessage, error, retry: subscribe };
}
